/**
 * translationJobsContext — the context, hook, and types for translation jobs.
 *
 * Kept separate from the provider COMPONENT (translationJobs.tsx) so that file
 * can export only a component — required for Vite Fast Refresh / HMR to work
 * (a file mixing a component with a hook export disables Fast Refresh for it).
 */
import { createContext, useContext } from 'react'
import type { TranslateStep } from './translate'

export interface TranslationJob {
  id: string
  courseId: string
  title: string
  source: string
  target: string
  step: TranslateStep
  progress: number
  status: 'running' | 'done' | 'error' | 'cancelled'
  newCourseId?: string
  error?: string
}

/** Why a startJob call was refused (no id returned). */
export type StartRefusal = 'not-signed-in' | 'duplicate' | 'at-capacity'

export interface TranslationJobsCtx {
  jobs: TranslationJob[]
  /** Returns the new job id, or a refusal reason. */
  startJob: (args: { courseId: string; title: string; source: string; target: string })
    => { id: string } | { refused: StartRefusal }
  cancelJob: (id: string) => void
  dismissJob: (id: string) => void
  /** Target language codes with an in-flight OR completed job for a course. */
  activeTargetsFor: (courseId: string) => string[]
  /** Target codes with a still-RUNNING job for a course (subset of active). */
  runningTargetsFor: (courseId: string) => string[]
  /** How many translations are running right now (across all courses). */
  runningCount: number
  /** Max translations allowed to run at once. */
  maxConcurrent: number
}

export const TranslationJobsContext = createContext<TranslationJobsCtx | null>(null)

export function useTranslationJobs(): TranslationJobsCtx {
  const v = useContext(TranslationJobsContext)
  if (!v) throw new Error('useTranslationJobs must be used within TranslationJobProvider')
  return v
}
